import {
  faEye,
  faPencil,
  faUserMinus,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons'
import React, { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  Avatar,
  Content,
  Data,
  Form,
  Indicators,
  Layout,
  Modal,
  Navigation,
  Responsive,
  theme,
} from 'ui'
import {
  useAdminUser,
  useDemoteUser,
  usePromoteUser,
  AccessLevel,
} from '../../../queries'
import { nameForUser, profileForUser, profileUrlForUser } from './helpers'
import { usePaginatedRoles } from './usePaginatedRoles'

import { AdminRoleNavigationDrawer } from './AdminRoleNavigationDrawer'

const { Attribute, Section } = Data

export const AdminRoleDetailView: React.FC = () => {
  // Drawer appearance
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('compact')

  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { search } = usePaginatedRoles()
  const { loading, data, error: loadError } = useAdminUser({ userId: id ?? '' })
  const user = data?.response?.user

  // Add state for confirmation modal
  const [confirmAction, setConfirmAction] = useState<
    'promote' | 'demote' | null
  >(null)

  // Add mutations
  const demoteUser = useDemoteUser()
  const promoteUser = usePromoteUser()

  const handleEditClick = () => {
    navigate(`/admin/roles/user/edit/${id}${search}`)
  }

  const handleViewProfileClick = () => {
    if (user) {
      navigate(profileUrlForUser(user))
    }
  }

  // Add handlers
  const showPromoteConfirmation = () => setConfirmAction('promote')
  const showDemoteConfirmation = () => setConfirmAction('demote')
  const dismissModal = () => setConfirmAction(null)

  const handleConfirmRoleChange = async () => {
    if (!id) return

    try {
      if (confirmAction === 'promote') {
        await promoteUser.mutateAsync(id)
      } else if (confirmAction === 'demote') {
        await demoteUser.mutateAsync(id)
      }
      dismissModal()
    } catch (error) {
      console.error('Role change failed:', error)
      // Error toast is handled by mutation
    }
  }

  const isMutating = demoteUser.isPending || promoteUser.isPending
  const isCurrentlyAdmin = user?.accessLevel === AccessLevel.Admin

  return (
    <div
      className="w-full flex-grow flex flex-col flex sm:flex-row flex"
      style={{ opacity: loading || isMutating ? 0.5 : 1 }}
    >
      <Helmet title={`${user?.firstName ?? 'User'} - MCSS Pro Connect`} />
      <AdminRoleNavigationDrawer />
      <Layout.PaddedContent>
        <div className="flex-col flex flex sm:flex-row flex">
          <article className="w-full flex flex-col flex mr-0 sm:mr-4">
            <Content.Body>
              <Navigation.ReturnLink to={`/admin/roles${search}`}>
                Return to Users
              </Navigation.ReturnLink>
            </Content.Body>
            <Content.Heading>
              {loading ? (
                <div className="flex">
                  <div className="mr-auto">
                    <Indicators.Spinner />
                  </div>
                </div>
              ) : (
                user?.emailAddress ?? 'n/a'
              )}
            </Content.Heading>

            {loadError ? <Form.ErrorComponent error={loadError.message} /> : ''}

            <Section>
              <Attribute
                label="Access Level:"
                value={loading ? '-------------' : user?.accessLevel}
              />
              <Attribute label="Associated Profile:">
                <Link to={profileUrlForUser(user)}>{profileForUser(user)}</Link>
              </Attribute>
            </Section>
          </article>
          <div className="flex-col flex mr-0 sm:mr-2 mb-auto w-full sm:w-1/2 md:w-1/3 mt-2 md:mt-4">
            <div className="mx-auto mb-2">
              {loading ? (
                <Indicators.Spinner size="4x" />
              ) : (
                <Avatar.Simple
                  color={theme.colorActionable}
                  name={nameForUser(user)}
                  maxInitials={1}
                  size={128}
                />
              )}
            </div>
            <Content.ActionButton
              label="Edit"
              icon={faPencil}
              onClick={handleEditClick}
              className="mt-0"
            />
            <Content.ActionButton
              label="View Profile"
              icon={faEye}
              onClick={handleViewProfileClick}
              className="mt-2"
              disabled={loading || !user}
            />

            {/* Add promote/demote buttons */}
            {!loading && user ? (
              <>
                {isCurrentlyAdmin ? (
                  <Content.ActionButton
                    label="Demote to User"
                    icon={faUserMinus}
                    onClick={showDemoteConfirmation}
                    appearance="destructive"
                    loading={isMutating}
                    className="mt-2"
                  />
                ) : (
                  <Content.ActionButton
                    label="Promote to Admin"
                    icon={faUserPlus}
                    onClick={showPromoteConfirmation}
                    appearance="actionable"
                    loading={isMutating}
                    className="mt-2"
                  />
                )}
              </>
            ) : null}
          </div>
        </div>
      </Layout.PaddedContent>

      {/* Confirmation Modal */}
      {confirmAction && user ? (
        <Modal.Dialog
          title={
            confirmAction === 'promote'
              ? 'Promote to Admin?'
              : 'Demote to User?'
          }
          message={
            confirmAction === 'promote'
              ? `Are you sure you want to promote ${user.emailAddress} to admin? This will grant them full administrative access to the system.`
              : `Are you sure you want to demote ${user.emailAddress} from admin? They will lose all administrative privileges.`
          }
          onDismiss={dismissModal}
          onConfirm={handleConfirmRoleChange}
          destructive={confirmAction === 'demote'}
          loading={isMutating}
          confirmLabel={confirmAction === 'promote' ? 'Promote' : 'Demote'}
        />
      ) : null}
    </div>
  )
}
